import { FileCopy } from '@mui/icons-material';
import { Box, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Cafe } from '../../apiTypes';
import Form from '../../components/forms/Form';
import FormColorPicker from '../../components/forms/FormColorPicker';
import FormSwitch from '../../components/forms/FormSwitch';
import FormTextField from '../../components/forms/FormTextField';
import FormUploadImg from '../../components/FormUploadImg';
import { PlusFeature, ProFeature } from '../../components/PlanCheck/PlanCheckPage';
import PrimaryButton from '../../components/PrimaryButton';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { useSnackbar } from '../../contexts/snackbar.context';
import { updateDoc } from '../../utils/api';
import WorkHours from './WorkHours/WorkHours';

const QRMenu = () => {
  const { cafeSlug, cafe } = useAdmin();
  const { addSnackbar } = useSnackbar();
  const { lang } = useLocale();
  const link = `https://firemenu.app/qr/${cafeSlug}/`;

  const form = useForm<NonNullable<Cafe['qrMenu']>>({
    defaultValues: {
      backgroundUrl: cafe?.qrMenu?.backgroundUrl || '',
      address: cafe?.qrMenu?.address || '',
      phone: cafe?.qrMenu?.phone || '',
      instagram: cafe?.qrMenu?.instagram || '',
      facebook: cafe?.qrMenu?.facebook || '',
      tableBooking: cafe?.qrMenu?.tableBooking || false,
      leaveReviews: cafe?.qrMenu?.leaveReviews || false,
      hasDelivery: cafe?.qrMenu?.hasDelivery || false,
      hasSelfOrder: cafe?.qrMenu?.hasSelfOrder || false,
      hasTableOrder: cafe?.qrMenu?.hasTableOrder || false,
      deliveryTerms: cafe?.qrMenu?.deliveryTerms || '',
      telegramId: cafe?.qrMenu?.telegramId || '',
      bgColor: cafe?.qrMenu?.bgColor || '#f8f9fa',
      primaryColor: cafe?.qrMenu?.primaryColor || '#222222',
      checkComponents: cafe?.qrMenu?.checkComponents || false,
      bank: cafe?.qrMenu?.bank || 'mono',
      bankAPIToken: cafe?.qrMenu?.bankAPIToken || '',
      serviceAlert: cafe?.qrMenu?.serviceAlert || false,
    },
  });

  const hasDelivery = form.watch('hasDelivery');
  const hasTableOrder = form.watch('hasTableOrder');

  const handleDownload = () => {
    const canvas = document.getElementById('QRCode') as HTMLCanvasElement;
    const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${cafe?.name} qrcode.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link);
    addSnackbar(t.copied[lang]);
  };

  const submit = useCallback(
    async (values: NonNullable<Cafe['qrMenu']>) => {
      await updateDoc<Cafe>(`cafes/${cafeSlug}`, {
        qrMenu: values,
      });
      form.reset(values);
    },
    [cafeSlug, form]
  );

  return (
    <Form form={form} onSubmit={submit}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <PrimaryButton variant="contained" type="submit" disabled={!form.formState.isDirty}>
          {t.save_changes[lang]}
        </PrimaryButton>
      </Box>

      <Grid container spacing={5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 5 }}>
            <QRCodeCanvas id="QRCode" size={200} style={{ width: '200px', height: '200px' }} value={link} />
            <PrimaryButton variant="text" onClick={handleDownload} sx={{ mt: 1, width: 200 }} color="secondary">
              {t.download[lang]} QR Code
            </PrimaryButton>
          </Box>

          <StyledTextField
            label={t.online_menu_link[lang]}
            variant="outlined"
            defaultValue={link}
            size="small"
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyLink}>
                    <FileCopy />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormTextField name="address" label={t.address[lang]} fullWidth size="small" sx={{ mt: 2 }} />

          <FormTextField name="phone" label={t.phone[lang]} fullWidth size="small" sx={{ mt: 2 }} />

          <FormTextField name="instagram" label={t.instagram_link[lang]} fullWidth size="small" sx={{ mt: 2 }} />

          <FormTextField name="facebook" label={t.facebook_link[lang]} fullWidth size="small" sx={{ mt: 2 }} />

          <FormTextField
            name="telegramId"
            label={`Telegram ID (${t.telegram_notifications[lang]})`}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 3 }}>
            {!!cafe?.bill?.defaultService && (
              <FormSwitch name="serviceAlert" label={t.display_service_charge_message[lang]} />
            )}

            <FormSwitch name="leaveReviews" label={t.visitor_reviews[lang]} />

            <PlusFeature>
              <FormSwitch name="tableBooking" label={t.online_table_booking[lang]} />
            </PlusFeature>

            <ProFeature>
              <FormSwitch name="hasTableOrder" label={t.ordering_and_payment[lang]} />
            </ProFeature>

            {hasTableOrder && (
              <FormTextField
                name="bankAPIToken"
                label={t.token_for_internet_acquiring[lang]}
                fullWidth
                size="small"
                sx={{ mb: 2, mt: 1 }}
              />
            )}

            <ProFeature>
              <FormSwitch name="hasDelivery" label={t.delivery[lang]} />
            </ProFeature>

            {hasDelivery && (
              <FormTextField
                name="deliveryTerms"
                multiline
                label={t.delivery_terms[lang]}
                fullWidth
                size="small"
                sx={{ mb: 2, mt: 1 }}
              />
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="body2" color={(t) => t.palette.text.secondary}>
            {t.background_image[lang]}
          </Typography>

          <FormUploadImg name="backgroundUrl" folder="qrMenuBG" size={800} width={300} height={150} />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormColorPicker color="secondary" label={t.primary_color[lang]} name="primaryColor" size="small" />

            <FormColorPicker color="secondary" label={t.background_color[lang]} name="bgColor" size="small" />
          </Box>

          <Typography variant="body2" color={(t) => t.palette.text.secondary} sx={{ mb: 1, mt: 3 }}>
            {t.working_hours[lang]}
          </Typography>
          <WorkHours />
        </Grid>
      </Grid>
    </Form>
  );
};

export default QRMenu;
